/**
 * LandingPage — public marketing splash served at "/".
 *
 * Structure:
 *   - Sticky header with "Mosaics" wordmark + "Powered by Premise" tagline,
 *     anchor navigation, and a Login CTA on the right.
 *   - Hero: animated gradient background, headline, sub-head, primary CTA.
 *   - About Us
 *   - Solutions (tabbed: Commercial / Government)
 *   - Resources (Product Data Sheets · White Papers · News & Events)
 *   - Contact Us (POSTs to /api/contact)
 *   - Footer
 *
 * Design choices:
 *   - No external animation library. Native CSS keyframes + a small
 *     IntersectionObserver hook (useScrollReveal) for fade-ins.
 *   - Respects `prefers-reduced-motion`.
 *   - Signed-in users are auto-redirected to /map — the marketing splash
 *     is for logged-out visitors only.
 *
 * Content is intentionally placeholder copy. Search for `TODO: content`
 * to find the copy blocks meant to be revised by marketing before launch.
 */

import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getFirebaseAuth } from '../firebase';
import { useScrollReveal } from '../hooks/useScrollReveal';

// ── Content (TODO: content) ─────────────────────────────────────────────
// Placeholder copy — swap for marketing-approved text before launch.

const HERO_HEADLINE = 'Know First. Own Next.';
const HERO_SUBHEAD =
  'In the increasingly perilous mosaic of global risk we combine real time actionable, geospatial intelligence with 24/7 on the ground human surveillance and response capability to predict and manage multiple, complex and escalating incidents with AI precision guidance to ensure the degree of organizational resilience and operational continuity to outmatch the competition in times of crisis.';

const ABOUT_COPY = [
  'Where others see fear and uncertainty we see opportunity. Governments and commercial enterprises alike receive tens of thousands of threat alerts daily - all too often triggered by fragmented data from disparate systems - making it difficult to understand the exact scope of the threat and thus paralyzing business critical decision making with outdated security protocols that conspire to induce operational gridlock. Mosaics full spectrum, multi-layered, analyst platform is purpose built on top of the global Premise contributor network - combining open source signals with on-the-ground operators into a single live, map driven view to the analysts, operators, and decision-makers who need to know first.',
];

const ABOUT_STATS = [
  { value: '140+', label: 'Countries' },
  { value: '7M+', label: 'Contributors' },
  { value: '2.3M+', label: 'Points of Interest' },
];

const SOLUTIONS_SUBHEAD = [
    'Government institutions and commercial enterprises operate in the face of an increasingly perilous and ever changing mosaic of global risk. The Mosaics platform provides advanced, ground truth situational awareness and decision grade intelligence against the backdrop of an increasingly unpredictable world.'
];

const COMMERCIAL_BULLETS = [
  'Mosaics closes critical international data and information gaps by providing vital early warning and threat-protection identification services trusted by both the US Federal Government and Department of War. We deliver verifiable ground truth by combining AI precision-guided open source singles with our network of 7 million+ contributors across 140 countries to provide locally-verified visual evidence, telemetry and brand sentiment data. We provide a single and complete view of the enterprise, empowering global industry leaders to mitigate asymmetric threats and minimize vulnerabilities to protect and secure their most critical assets in the markets that matter most.',
];

const GOVERNMENT_BULLETS = [
  'Mosaics closes critical international data and information gaps by providing vital early warning and threat-protection identification services to the US Department of War, US Federal government and US international partners.  We deliver verifiable ground truth using our network of 7 million+ contributors across 140 countries to provide locally-verified visual evidence, telemetry and sentiment data. We also provide AI-driven and Human-in-the-Loop (HITL) solutions, and we integrate with and complement existing government systems to provide a single and complete view of the operating picture to mitigate asymmetric threats and minimize vulnerabilities to protect and secure our most critical assets wherever they are deployed.',
];

interface ResourceCard {
  title: string;
  blurb: string;
  href: string;
}

// TODO: content — replace href="#" with real CRM landing-page URLs once the CRM is picked.
const DATA_SHEETS: ResourceCard[] = [
  { title: 'Mosaics Platform Overview', blurb: 'A one-page summary of what Mosaics does and who it is for.', href: '#' },
  { title: 'Premise Network Data Sheet', blurb: 'How the contributor network is sized, tiered, and quality-controlled.', href: '#' },
  { title: 'Risk & Alerting Add-On', blurb: 'Automated news + social event detection and severity scoring.', href: '#' },
];

const WHITE_PAPERS: ResourceCard[] = [
  { title: 'Measuring the Immeasurable', blurb: 'A field-tested methodology for observation-based ground truth in fragile regions.', href: '#' },
  { title: 'Crowdsourced Signals in National Security', blurb: 'Case studies from analysts using contributor data alongside SIGINT and OSINT.', href: '#' },
];

const NEWS_ITEMS: ResourceCard[] = [
  { title: 'Premise announces Mosaics 2.0', blurb: 'Rebuilt map, integrated AI analyst, new alerting.', href: '#' },
  { title: 'Coverage: how ground-truth data changed a policy debate', blurb: 'Featured in a leading policy publication.', href: '#' },
  { title: 'Upcoming: AFCEA West 2026', blurb: 'Come find us at booth #TBD to see Mosaics live.', href: '#' },
];

// ── Palette (keeps the marketing page in the same visual family as the app) ─
const c = {
  bgDeep: '#00203F',
  bgMid: '#004777',
  bgLight: '#006B88',
  accent: '#5eead4',
  accentDeep: '#008080',
  accentGreen: '#2E8B57',
  text: '#ffffff',
  textDim: '#cbd5e1',
  textMuted: '#94a3b8',
  border: 'rgba(255, 255, 255, 0.12)',
  surface: 'rgba(255, 255, 255, 0.04)',
};

const NAV_LINKS: { label: string; href: string }[] = [
  { label: 'Home', href: '#home' },
  { label: 'About', href: '#about' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Resources', href: '#resources' },
  { label: 'Contact', href: '#contact' },
];

// ── Component ───────────────────────────────────────────────────────────

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Auth-state watcher — we don't gate the page, but we DO redirect signed-in
  // visitors straight to /map so they skip the marketing splash.
  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      const auth = await getFirebaseAuth();
      if (cancelled) return;
      unsub = onAuthStateChanged(auth, (u) => {
        setUser(u);
        setAuthResolved(true);
      });
    })();
    return () => { cancelled = true; if (unsub) unsub(); };
  }, []);

  // Header shrinks / darkens after any scroll — subtle but marks the transition.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Unlock body/html scroll while the landing is mounted. index.html sets
  // `html, body, #root { height: 100%; overflow: hidden }` so the map page
  // can occupy the full viewport as a fixed layout — that global rule
  // prevents this scrollable marketing page from scrolling. Restore the
  // original values on unmount so the map's assumptions still hold.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow ?? '',
      rootHeight: root?.style.height ?? '',
    };
    html.style.overflow = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.height = 'auto';
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
    }
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  // Re-honor the URL hash after we've unlocked scroll above. The browser's
  // built-in "scroll to #anchor on load" fires before this component's
  // useEffect can run, and at that moment html/body/#root are still
  // overflow:hidden from index.html — so the automatic scroll no-ops and
  // the user lands on the top of the page. This effect scrolls into view
  // once the page is actually scrollable. Fixes the "Contact us to get
  // started" CTA on /explore/venezuela which links to /#contact.
  //
  // Retry ladder: rAF (fast path) → window 'load' (media done) → 900ms
  // (reveal animations settled). The hero has an autoplaying 5MB video
  // whose metadata alone shifts the layout hundreds of pixels down; a
  // one-shot rAF scroll lands well short of the target once the video
  // paints. We re-scroll on each ladder rung so the final position is
  // always the settled one.
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const id = hash.slice(1);
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'auto', block: 'start' });
    };
    const raf1 = requestAnimationFrame(() => requestAnimationFrame(scroll));
    // window.load fires after images/video-metadata have loaded — the
    // biggest source of post-mount layout shift on this page.
    const onLoad = () => scroll();
    if (document.readyState === 'complete') {
      // Already loaded (e.g. warm-cache reload) — schedule as microtask
      // so it still runs after our initial rAF.
      Promise.resolve().then(scroll);
    } else {
      window.addEventListener('load', onLoad, { once: true });
    }
    // Belt-and-suspenders: reveal-animation sections translate into place
    // over 400-600ms via IntersectionObserver; 900ms lets everything above
    // #contact settle at its final height.
    const settleT = window.setTimeout(scroll, 900);

    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener('load', onLoad);
      window.clearTimeout(settleT);
    };
  }, []);

  // Auto-redirect signed-in users to the app.
  if (authResolved && user) {
    return <Navigate to="/map" replace />;
  }

  return (
    <div style={styles.root}>
      <style>{KEYFRAMES}</style>

      <Header scrolled={scrolled} onLoginClick={() => navigate('/login')} />

      <main>
        <Hero
          onPrimaryClick={() => navigate('/explore/venezuela')}
          onSecondaryClick={() => navigate('/login')}
        />
        <TryFreeDemo onClick={() => navigate('/explore/venezuela')} />
        <About />
        <Solutions />
        <Resources />
        <Contact />
      </main>

      <Footer />
    </div>
  );
};

export default LandingPage;

// ── Header ──────────────────────────────────────────────────────────────

const Header: React.FC<{ scrolled: boolean; onLoginClick: () => void }> = ({
  scrolled,
  onLoginClick,
}) => (
  <header
    style={{
      ...styles.header,
      background: scrolled ? 'rgba(0, 32, 63, 0.92)' : 'rgba(0, 32, 63, 0.55)',
      borderBottomColor: scrolled ? c.border : 'transparent',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
    }}
  >
    <div style={styles.headerInner}>
      {/* alignItems: center makes the "Powered by Premise" tag sit
          horizontally centered under the wordmark. The container's width
          is bounded by the wordmark (its widest child) so the tag centers
          within the wordmark's width, not the whole header. */}
      <a href="#home" style={{ ...styles.brand, alignItems: 'center' }}>
        <img src="/mosaics_word.png" alt="Mosaics" style={styles.brandWordLarge} />
        <PoweredByPremise />
      </a>

      {/* Anchor nav — hidden on narrow viewports (see KEYFRAMES media rule
          for .landing-header-nav). On mobile the page already scrolls,
          so users reach sections by scrolling; only the brand + Login
          stay in the top bar. */}
      <nav className="landing-header-nav" style={styles.nav}>
        {NAV_LINKS.map((link) => (
          <a key={link.href} href={link.href} style={styles.navLink}>
            {link.label}
          </a>
        ))}
      </nav>

      <button
        style={styles.loginBtn}
        onClick={onLoginClick}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0, 128, 128, 0.5)';
          (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 14px rgba(0, 128, 128, 0.4)';
          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        }}
      >
        Sign in
      </button>
    </div>
  </header>
);

// ── Hero ────────────────────────────────────────────────────────────────

const Hero: React.FC<{
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
}> = ({ onPrimaryClick, onSecondaryClick }) => (
  <section id="home" style={styles.hero}>
    {/* Animated background — gradient sweep behind a field of floating orbs. */}
    <div style={styles.heroBg} aria-hidden>
      <div style={styles.heroGradient} />
      <div style={{ ...styles.orb, ...styles.orb1 }} />
      <div style={{ ...styles.orb, ...styles.orb2 }} />
      <div style={{ ...styles.orb, ...styles.orb3 }} />
      <div style={styles.heroGrid} />
    </div>

    <div style={styles.heroContent}>
      {/* Hex-globe emblem — sits above the eyebrow tag, gently floating.
          Alt is empty because the wordmark right below already conveys
          the brand to screen readers. */}
      <img src="/mosaics_logo_optimized.png" alt="" style={styles.heroEmblem} />
      <h1 style={styles.heroHeadline}>{HERO_HEADLINE}</h1>
      <p style={styles.heroSubhead}>{HERO_SUBHEAD}</p>
      <div style={styles.heroCtaRow}>
        {/* Primary: free tier — no login, Venezuela only. Secondary: sign in. */}
        <button
          style={styles.heroCta}
          onClick={onPrimaryClick}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 32px rgba(0, 128, 128, 0.55)';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 24px rgba(0, 128, 128, 0.45)';
            (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
          }}
        >
          Try Mosaics free →
        </button>
        <button
          type="button"
          onClick={onSecondaryClick}
          style={{ ...styles.heroSecondaryCta, cursor: 'pointer', fontFamily: 'inherit' }}
        >
          Sign in
        </button>
      </div>
    </div>
  </section>
);

// ── Try-Free demo (video with hover-to-CTA) ────────────────────────────
// Autoplaying loop of the app in action. Hovering fades the video out and
// reveals a "Click to Try For Free" call-to-action; clicking anywhere on
// the card sends the visitor to /explore/venezuela. Muted + playsInline
// are required for autoplay on mobile Safari and Chrome.
const TryFreeDemo: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const [ref, visible] = useScrollReveal<HTMLElement>();
  const [hovered, setHovered] = React.useState(false);
  return (
    <section
      ref={ref}
      style={{
        ...styles.section,
        ...revealStyle(visible),
        paddingTop: 80,
        paddingBottom: 80,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        aria-label="Try Mosaics Now"
        style={{
          position: 'relative',
          display: 'block',
          // Fill the section's content width (styles.section is max 1100px
          // with 32px inline padding) so the video card lines up with the
          // About / Solutions / Resources sections below it.
          width: '100%',
          padding: 0,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 32, 63, 0.25)',
        }}
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          // Preload metadata only — the file is ~5MB, no need to eagerly
          // pull the whole thing before the section scrolls into view.
          preload="metadata"
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            // Keep the video mostly visible on hover — the overlay adds
            // its own tint on top, so a light dim is enough to signal state.
            opacity: hovered ? 0.75 : 1,
            transition: 'opacity 0.25s ease-out',
          }}
        >
          <source src="/FreeMosaics.mp4" type="video/mp4" />
        </video>
        <div
          // Overlay: sits above the video, matches its rounded corners
          // (via parent overflow:hidden), fades in on hover. Kept light so
          // the video still reads through it; the text carries the message
          // and picks up a text-shadow for legibility over any frame.
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 32, 63, 0.25)',
            color: c.text,
            fontSize: 'clamp(18px, 2.2vw, 26px)',
            fontWeight: 700,
            letterSpacing: 0.3,
            textShadow: '0 2px 12px rgba(0, 0, 0, 0.65)',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.25s ease-out',
            pointerEvents: 'none',
          }}
        >
          Try Mosaics Now
        </div>
      </button>
    </section>
  );
};

// ── About ───────────────────────────────────────────────────────────────

const About: React.FC = () => {
  const [ref, visible] = useScrollReveal<HTMLElement>();
  return (
    <section
      id="about"
      ref={ref}
      style={{ ...styles.section, ...revealStyle(visible) }}
    >
      <SectionEyebrow>About</SectionEyebrow>
      <h2 style={styles.sectionHeadline}>Ground truth, at global scale.</h2>
      <div style={styles.aboutBody}>
        {ABOUT_COPY.map((para, i) => (
          <p key={i} style={styles.paragraph}>{para}</p>
        ))}
      </div>
      <div style={styles.statsRow}>
        {ABOUT_STATS.map((s) => (
          <div key={s.label} style={styles.statTile}>
            <div style={styles.statValue}>{s.value}</div>
            <div style={styles.statLabel}>{s.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ── Solutions ───────────────────────────────────────────────────────────

const Solutions: React.FC = () => {
  const [ref, visible] = useScrollReveal<HTMLElement>();
  const [tab, setTab] = useState<'commercial' | 'government'>('commercial');
  const bullets = tab === 'commercial' ? COMMERCIAL_BULLETS : GOVERNMENT_BULLETS;

  return (
    <section
      id="solutions"
      ref={ref}
      style={{ ...styles.section, ...revealStyle(visible) }}
    >
      <SectionEyebrow>Solutions</SectionEyebrow>
      <h2 style={styles.sectionHeadline}>Omniscience. Delivered.</h2>

      <div style={styles.aboutBody}>
        {SOLUTIONS_SUBHEAD.map((para, i) => (
          <p key={i} style={styles.paragraph}>{para}</p>
        ))}
      </div>

      <div style={styles.tabRow}>
        <button
          style={{ ...styles.tabButton, ...(tab === 'commercial' ? styles.tabButtonActive : {}) }}
          onClick={() => setTab('commercial')}
        >
          Commercial
        </button>
        <button
          style={{ ...styles.tabButton, ...(tab === 'government' ? styles.tabButtonActive : {}) }}
          onClick={() => setTab('government')}
        >
          Government
        </button>
      </div>

      <ul style={styles.bulletList}>
        {bullets.map((b, i) => (
          <li key={`${tab}-${i}`} style={styles.bulletItem}>
            <span style={styles.bulletMark} aria-hidden>▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

// ── Resources ───────────────────────────────────────────────────────────

const Resources: React.FC = () => {
  const [ref, visible] = useScrollReveal<HTMLElement>();
  return (
    <section
      id="resources"
      ref={ref}
      style={{ ...styles.section, ...revealStyle(visible) }}
    >
      <SectionEyebrow>Resources</SectionEyebrow>
      <h2 style={styles.sectionHeadline}>Dig deeper.</h2>

      <ResourceGroup title="Product Data Sheets" cards={DATA_SHEETS} />
      <ResourceGroup title="White Papers" cards={WHITE_PAPERS} />
      <ResourceGroup title="News & Events" cards={NEWS_ITEMS} />
    </section>
  );
};

const ResourceGroup: React.FC<{ title: string; cards: ResourceCard[] }> = ({ title, cards }) => (
  <div style={styles.resourceGroup}>
    <h3 style={styles.resourceGroupTitle}>{title}</h3>
    <div style={styles.cardGrid}>
      {cards.map((card, i) => (
        <a
          key={i}
          href={card.href}
          target={card.href.startsWith('#') ? undefined : '_blank'}
          rel={card.href.startsWith('#') ? undefined : 'noopener noreferrer'}
          style={styles.card}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)';
            (e.currentTarget as HTMLElement).style.borderColor = 'rgba(94, 234, 212, 0.5)';
            (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 40px rgba(0, 128, 128, 0.25)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
            (e.currentTarget as HTMLElement).style.borderColor = c.border;
            (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          }}
        >
          <div style={styles.cardTitle}>{card.title}</div>
          <div style={styles.cardBlurb}>{card.blurb}</div>
          <div style={styles.cardArrow}>Read more →</div>
        </a>
      ))}
    </div>
  </div>
);

// ── Contact ─────────────────────────────────────────────────────────────

const Contact: React.FC = () => {
  const [ref, visible] = useScrollReveal<HTMLElement>();
  const [form, setForm] = useState({ name: '', company: '', email: '', message: '' });
  // Honeypot — real users won't fill this hidden field, bots probably will.
  const [botField, setBotField] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setErrMsg(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, _hp: botField }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Submission failed (${res.status})`);
      }
      setStatus('sent');
      setForm({ name: '', company: '', email: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrMsg((err as Error).message);
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      style={{ ...styles.section, ...revealStyle(visible) }}
    >
      <SectionEyebrow>Contact</SectionEyebrow>
      <h2 style={styles.sectionHeadline}>Let&rsquo;s talk.</h2>
      <p style={styles.paragraph}>
        Tell us about your team and what you&rsquo;re trying to see. We&rsquo;ll set up a working session.
      </p>

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Honeypot — hidden from real users via aria-hidden + off-screen positioning. */}
        <label aria-hidden style={styles.honeypot} tabIndex={-1}>
          Website
          <input
            type="text"
            value={botField}
            onChange={(e) => setBotField(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </label>

        <div style={styles.formRow}>
          <FormInput label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <FormInput label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
        </div>
        <FormInput label="Work email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
        <FormTextarea label="What are you trying to see?" value={form.message} onChange={(v) => setForm({ ...form, message: v })} required />

        <button
          type="submit"
          style={{
            ...styles.formSubmit,
            opacity: status === 'sending' ? 0.7 : 1,
            cursor: status === 'sending' ? 'wait' : 'pointer',
          }}
          disabled={status === 'sending'}
        >
          {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Sent — we’ll be in touch.' : 'Send message'}
        </button>

        {status === 'error' && (
          <div style={styles.formError}>
            {errMsg || 'Something went wrong. Please try again or email us directly.'}
          </div>
        )}
      </form>
    </section>
  );
};

const FormInput: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
}> = ({ label, value, onChange, type = 'text', required }) => (
  <label style={styles.formLabel}>
    <span style={styles.formLabelText}>{label}{required && <span style={styles.formLabelReq}> *</span>}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={styles.formInput}
      maxLength={200}
    />
  </label>
);

const FormTextarea: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}> = ({ label, value, onChange, required }) => (
  <label style={styles.formLabel}>
    <span style={styles.formLabelText}>{label}{required && <span style={styles.formLabelReq}> *</span>}</span>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      style={styles.formTextarea}
      rows={5}
      maxLength={2000}
    />
  </label>
);

// ── Footer ──────────────────────────────────────────────────────────────

const Footer: React.FC = () => (
  <footer style={styles.footer}>
    <div style={styles.footerInner}>
      <a
        href="https://www.culmen.com"
        target="_blank"
        rel="noopener noreferrer"
        style={styles.footerCulmenLink}
        aria-label="Culmen International"
      >
        <img src="/culmen_white_sm.png" alt="Culmen International" style={styles.footerCulmenLogo} />
      </a>
      <div style={styles.footerLinks}>
        <a href="#about" style={styles.footerLink}>About</a>
        <a href="#solutions" style={styles.footerLink}>Solutions</a>
        <a href="#resources" style={styles.footerLink}>Resources</a>
        <a href="#contact" style={styles.footerLink}>Contact</a>
        <Link to="/login" style={styles.footerLink}>Sign in</Link>
      </div>
      <div style={styles.footerCopy}>
        © {new Date().getFullYear()} Premise Data · All rights reserved.
      </div>
    </div>
  </footer>
);

// ── Small helpers ───────────────────────────────────────────────────────

// The Premise wallet logo (white WebP served from client/public). Rendered
// small next to the "Powered by" tag under the Mosaics wordmark. Alt is
// spelled out for screen readers since the visual is image-only.
const PREMISE_LOGO_URL = '/premise_wallet-logo_sm.webp';

const PoweredByPremise: React.FC = () => (
  <div style={styles.poweredBy}>
    <span style={styles.poweredByText}>Powered by</span>
    <img src={PREMISE_LOGO_URL} alt="Premise" style={styles.poweredByLogo} />
  </div>
);

const SectionEyebrow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={styles.sectionEyebrow}>
    <span style={styles.sectionEyebrowMark} />
    <span>{children}</span>
  </div>
);

const revealStyle = (visible: boolean): React.CSSProperties => ({
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(24px)',
  transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
});

// Honeycomb tile (Hero Patterns "Hexagons", MIT-licensed). Renders as a
// proper touching honeycomb — the fill-rule='evenodd' trick cuts an
// outline inside a filled hex so we get thin, uniform lines that read
// as an outlined grid, not filled shapes. Tile is 28×49 pointy-top;
// tuned color to the app's teal accent.
const HEX_PATTERN_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'>
  <g fill-rule='evenodd'>
    <g fill='rgba(94,234,212,0.14)'>
      <path d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/>
    </g>
  </g>
</svg>`.replace(/\s+/g, ' ').trim();

const HEX_PATTERN_URL = `url("data:image/svg+xml;utf8,${encodeURIComponent(HEX_PATTERN_SVG)}")`;

// ── Keyframes (injected once at the top of the render) ──────────────────

const KEYFRAMES = `
  @keyframes mosaicsEmblemFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-8px); }
  }
  @keyframes mosaicsHeroSweep {
    0%   { transform: translate(-10%, -10%) rotate(0deg); }
    50%  { transform: translate(4%, 6%) rotate(180deg); }
    100% { transform: translate(-10%, -10%) rotate(360deg); }
  }
  @keyframes mosaicsOrbFloatA {
    0%,100% { transform: translate(0, 0) scale(1); }
    50%     { transform: translate(40px, -30px) scale(1.08); }
  }
  @keyframes mosaicsOrbFloatB {
    0%,100% { transform: translate(0, 0) scale(1); }
    50%     { transform: translate(-30px, 20px) scale(1.12); }
  }
  @keyframes mosaicsOrbFloatC {
    0%,100% { transform: translate(0, 0) scale(1); }
    50%     { transform: translate(20px, 40px) scale(0.94); }
  }
  @keyframes mosaicsGridPan {
    0%   { background-position: 0 0; }
    /* Move by exactly one tile so the seam is invisible.
       Must match backgroundSize on styles.heroGrid. */
    100% { background-position: 56px 98px; }
  }
  html { scroll-behavior: smooth; }
  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    * { animation: none !important; transition: none !important; }
  }
  /* Mobile: hide the anchor nav — brand + Login CTA still show. The page
     scrolls, so users get to each section by scrolling. */
  @media (max-width: 768px) {
    .landing-header-nav { display: none !important; }
  }
`;

// ── Styles ──────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: c.bgDeep,
    color: c.text,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: 1.6,
    overflowX: 'hidden',
  },

  // Header
  header: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    borderBottom: '1px solid transparent',
    transition: 'background 0.25s, border-color 0.25s',
  },
  headerInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  brand: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    textDecoration: 'none',
    color: c.text,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  brandIcon: {
    height: 34,
    width: 'auto',
    display: 'block',
    // The hex-globe PNG has a lot of visual weight — a subtle drop shadow
    // makes it feel like it belongs against the dark header rather than
    // sitting flat on top of it.
    filter: 'drop-shadow(0 2px 8px rgba(0, 128, 128, 0.35))',
  },
  brandWord: {
    height: 22,
    width: 'auto',
    display: 'block',
  },
  // Larger wordmark used in the header — height 36 so the "Powered by"
  // tag directly below reads at about the same visual width.
  brandWordLarge: {
    height: 36,
    width: 'auto',
    display: 'block',
  },
  poweredBy: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
  },
  poweredByText: {
    fontSize: 9,
    letterSpacing: 1.4,
    color: c.textMuted,
    textTransform: 'uppercase',
    fontWeight: 500,
  },
  poweredByLogo: {
    // The Premise wallet logo is white; on our dark header it stands out
    // on its own. Small size keeps it deferential to the Mosaics mark.
    height: 12,
    width: 'auto',
    display: 'block',
    opacity: 0.9,
  },
  nav: {
    marginLeft: 'auto',
    display: 'flex',
    gap: 28,
  },
  navLink: {
    color: c.textDim,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    transition: 'color 0.15s',
  },
  loginBtn: {
    // marginLeft:auto pushes the button to the right end of the header
    // regardless of whether the nav is present — matters on mobile where
    // the nav is hidden but Login must still hug the right edge.
    marginLeft: 'auto',
    padding: '10px 22px',
    border: 'none',
    borderRadius: 999,
    background: `linear-gradient(135deg, ${c.accentDeep}, ${c.accentGreen})`,
    color: c.text,
    fontSize: 14,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0, 128, 128, 0.4)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },

  // Hero
  hero: {
    position: 'relative',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '120px 32px 80px',
    overflow: 'hidden',
  },
  heroBg: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    background: `radial-gradient(circle at 20% 30%, ${c.bgLight} 0%, ${c.bgMid} 45%, ${c.bgDeep} 100%)`,
  },
  heroGradient: {
    position: 'absolute',
    inset: '-30%',
    background: `conic-gradient(from 0deg, transparent 0%, ${c.accentDeep}44 25%, transparent 50%, ${c.accentGreen}44 75%, transparent 100%)`,
    animation: 'mosaicsHeroSweep 30s linear infinite',
    filter: 'blur(80px)',
    opacity: 0.5,
  },
  // Honeycomb overlay. Two staggered hex outlines drawn in a 60×52 SVG
  // tile that repeats seamlessly. Picks up the Mosaics logo's hex motif
  // and gives the hero a subtle sense of depth without stealing focus
  // from the headline. Radial mask fades edges so the pattern doesn't
  // butt into the header or content below.
  heroGrid: {
    position: 'absolute',
    inset: 0,
    backgroundImage: HEX_PATTERN_URL,
    // Scaled up 2× from the source 28×49 tile so hexes read at a
    // comfortable size on the hero. Increase to make them larger,
    // decrease to make the mesh denser.
    backgroundSize: '56px 98px',
    animation: 'mosaicsGridPan 24s linear infinite',
    maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 75%)',
    WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 75%)',
  },
  orb: {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(60px)',
    opacity: 0.5,
  },
  orb1: {
    width: 320,
    height: 320,
    top: '10%',
    left: '15%',
    background: `radial-gradient(circle, ${c.accent}88, transparent 70%)`,
    animation: 'mosaicsOrbFloatA 14s ease-in-out infinite',
  },
  orb2: {
    width: 400,
    height: 400,
    top: '40%',
    right: '10%',
    background: `radial-gradient(circle, ${c.accentDeep}88, transparent 70%)`,
    animation: 'mosaicsOrbFloatB 18s ease-in-out infinite',
  },
  orb3: {
    width: 260,
    height: 260,
    bottom: '10%',
    left: '30%',
    background: `radial-gradient(circle, ${c.accentGreen}66, transparent 70%)`,
    animation: 'mosaicsOrbFloatC 22s ease-in-out infinite',
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 900,
    textAlign: 'center',
  },
  heroEmblem: {
    display: 'block',
    height: 240,
    width: 'auto',
    margin: '0 auto 24px',
    // Teal glow so the emblem feels lifted off the dark hero background
    // rather than pasted flat on top. Pairs with the mosaicsEmblemFloat
    // animation for a subtle "living" feel.
    filter: 'drop-shadow(0 12px 40px rgba(0, 128, 128, 0.45))',
    animation: 'mosaicsEmblemFloat 8s ease-in-out infinite',
  },
  heroEyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 14px',
    borderRadius: 999,
    background: c.surface,
    border: `1px solid ${c.border}`,
    fontSize: 11,
    letterSpacing: 1.4,
    color: c.textDim,
    marginBottom: 24,
  },
  heroEyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: c.accent,
    boxShadow: `0 0 12px ${c.accent}`,
  },
  heroHeadline: {
    fontSize: 'clamp(38px, 6vw, 65px)',
    fontWeight: 700,
    lineHeight: 1.15,
    margin: '0 0 20px',
    background: `linear-gradient(135deg, ${c.text} 0%, ${c.accent} 60%, ${c.text} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: -0.5,
  },
  heroSubhead: {
    fontSize: 'clamp(16px, 1.5vw, 20px)',
    color: c.textDim,
    margin: '0 auto 36px',
    maxWidth: 720,
  },
  heroCtaRow: {
    display: 'flex',
    gap: 16,
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  heroCta: {
    padding: '16px 36px',
    border: 'none',
    borderRadius: 999,
    background: `linear-gradient(135deg, ${c.accentDeep}, ${c.accentGreen})`,
    color: c.text,
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(0, 128, 128, 0.45)',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  heroSecondaryCta: {
    padding: '16px 30px',
    borderRadius: 999,
    color: c.text,
    textDecoration: 'none',
    fontSize: 16,
    fontWeight: 600,
    border: `1px solid ${c.border}`,
    background: c.surface,
    transition: 'background 0.15s, border-color 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
  },

  // Sections
  section: {
    position: 'relative',
    maxWidth: 1100,
    margin: '0 auto',
    padding: '120px 32px',
    // The fixed header (~64px) sits above the section top when we
    // scrollIntoView to a #hash anchor. scrollMarginTop tells the browser
    // to leave room for it, so the section eyebrow is visible after
    // landing rather than being tucked under the header.
    scrollMarginTop: 80,
  },
  sectionEyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 11,
    letterSpacing: 1.5,
    color: c.accent,
    textTransform: 'uppercase',
    fontWeight: 600,
    marginBottom: 16,
  },
  sectionEyebrowMark: {
    width: 24,
    height: 2,
    background: c.accent,
    borderRadius: 2,
  },
  sectionHeadline: {
    fontSize: 'clamp(28px, 4vw, 44px)',
    fontWeight: 700,
    lineHeight: 1.15,
    margin: '0 0 32px',
    letterSpacing: -0.3,
    maxWidth: 800,
  },
  paragraph: {
    fontSize: 17,
    color: c.textDim,
    margin: '0 0 16px',
  },

  // About
  aboutBody: {
    maxWidth: 780,
    marginBottom: 48,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 20,
  },
  statTile: {
    padding: '28px 24px',
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 14,
  },
  statValue: {
    fontSize: 42,
    fontWeight: 700,
    color: c.accent,
    lineHeight: 1,
    letterSpacing: -1,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 12,
    letterSpacing: 1.4,
    color: c.textMuted,
    textTransform: 'uppercase',
  },

  // Solutions
  tabRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 32,
    borderBottom: `1px solid ${c.border}`,
  },
  tabButton: {
    padding: '12px 22px',
    background: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    color: c.textDim,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    transition: 'color 0.15s, border-color 0.15s',
    marginBottom: -1,
  },
  tabButtonActive: {
    color: c.text,
    borderBottom: `2px solid ${c.accent}`,
  },
  bulletList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  bulletItem: {
    display: 'flex',
    gap: 14,
    fontSize: 17,
    color: c.textDim,
    padding: '14px 0',
    borderBottom: `1px solid ${c.border}`,
  },
  bulletMark: {
    color: c.accent,
    fontWeight: 700,
    flexShrink: 0,
  },

  // Resources
  resourceGroup: {
    marginBottom: 48,
  },
  resourceGroupTitle: {
    fontSize: 18,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: c.text,
    margin: '0 0 16px',
    paddingBottom: 8,
    borderBottom: `1px solid ${c.border}`,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 16,
  },
  card: {
    display: 'block',
    padding: '22px 24px',
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 14,
    color: c.text,
    textDecoration: 'none',
    transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
  },
  cardBlurb: {
    fontSize: 14,
    color: c.textDim,
    marginBottom: 12,
    lineHeight: 1.5,
  },
  cardArrow: {
    fontSize: 13,
    color: c.accent,
    fontWeight: 600,
  },

  // Contact form
  form: {
    marginTop: 32,
    maxWidth: 640,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  formLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  formLabelText: {
    fontSize: 12,
    letterSpacing: 0.6,
    color: c.textMuted,
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  formLabelReq: {
    color: c.accent,
  },
  formInput: {
    padding: '12px 14px',
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    color: c.text,
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  formTextarea: {
    padding: '12px 14px',
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    color: c.text,
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
    resize: 'vertical',
    minHeight: 120,
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  formSubmit: {
    marginTop: 8,
    padding: '14px 24px',
    border: 'none',
    borderRadius: 999,
    background: `linear-gradient(135deg, ${c.accentDeep}, ${c.accentGreen})`,
    color: c.text,
    fontSize: 15,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0, 128, 128, 0.4)',
    transition: 'transform 0.15s',
    alignSelf: 'flex-start',
  },
  formError: {
    padding: '10px 14px',
    background: 'rgba(220, 38, 38, 0.15)',
    border: '1px solid rgba(248, 113, 113, 0.35)',
    borderRadius: 8,
    color: '#fca5a5',
    fontSize: 14,
  },
  honeypot: {
    position: 'absolute',
    left: -9999,
    width: 1,
    height: 1,
    overflow: 'hidden',
    opacity: 0,
    pointerEvents: 'none',
  },

  // Footer
  footer: {
    marginTop: 80,
    borderTop: `1px solid ${c.border}`,
    background: 'rgba(0, 32, 63, 0.6)',
  },
  footerInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '32px',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 32,
    alignItems: 'center',
  },
  // Culmen link block in the footer left column — replaces the earlier
  // Mosaics logo/wordmark + Powered-by treatment. Links out to
  // culmen.com in a new tab; the logo is a 320×129 white mountain mark
  // that sits comfortably at ~44px tall.
  footerCulmenLink: {
    display: 'inline-flex',
    alignItems: 'center',
    textDecoration: 'none',
    transition: 'opacity 0.15s',
  },
  footerCulmenLogo: {
    height: 44,
    width: 'auto',
    display: 'block',
  },
  footerLinks: {
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 20,
  },
  footerLink: {
    color: c.textDim,
    textDecoration: 'none',
    fontSize: 14,
    transition: 'color 0.15s',
  },
  footerCopy: {
    fontSize: 12,
    color: c.textMuted,
  },
};
